/**
 * © 2025 Grupo Salamander SAPI de CV
 *
 * MuiChip component overrides for the Renaissance design system.
 *
 * The default chip is a low-saturation neutral pill (redesign canvas, ported
 * by plan task 1.1) so a dense list stays calm; the named colour variants
 * below still fill with their palette colour when a caller asks for one.
 */

import { colorConverter } from '../colorConverter'
import type { TPaletteDefinition } from '../palette'
import { focusRing } from '../focusRing'
import { onAccent } from '../hyper'
import { radius, sizing } from '../tokens'

export function chipOverrides(
  palette: TPaletteDefinition,
  mode: 'light' | 'dark' = 'light'
) {
  return {
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: radius.full,
          fontWeight: 500,
          border: 0,
          backgroundColor: colorConverter(
            palette.text.main,
            mode === 'dark' ? 0.12 : 0.06
          ),
          // A clickable/deletable chip renders via ButtonBase, so the ring
          // keys off `Mui-focusVisible` like every other ButtonBase-derived
          // role; it simply never shows on a purely decorative chip (no
          // ButtonBase, nothing focusable).
          '&.Mui-focusVisible': focusRing(palette, mode),
        },
      },
      variants: [
        {
          props: { size: 'small' as const },
          style: { fontSize: sizing.chipSmallFont, height: sizing.chipSmall },
        },
        {
          // `color: '#FFFFFF'` was hardcoded on every variant below
          // regardless of the fill (#228): several of these (purple, cyan,
          // teal, success/green, and `primary` for at least one real
          // instance accent) are light enough that white only clears
          // 1.9-4.1:1 against them. `onAccent` picks whichever of
          // black/white actually clears more contrast against the fill in
          // use, so a future palette colour (or a lighter per-instance
          // brand accent) gets the right pick automatically instead of
          // silently failing the same way.
          props: { color: 'primary' as const },
          style: {
            backgroundColor: palette.primary.main,
            color: onAccent(
              palette.primary.main,
              palette.primary.forcedContrastText
            ),
          },
        },
        {
          props: { color: 'secondary' as const },
          style: {
            backgroundColor: palette.mint.main,
            color: onAccent(palette.mint.main),
          },
        },
        {
          props: { color: 'success' as const },
          style: {
            backgroundColor: palette.success.main,
            color: onAccent(palette.success.main),
          },
        },
        {
          props: { color: 'error' as const },
          style: {
            backgroundColor: palette.error.main,
            color: onAccent(palette.error.main),
          },
        },
        {
          props: { color: 'warning' as const },
          style: {
            backgroundColor: palette.warning.main,
            color: palette.text.light, // Dark text for WCAG on yellow
          },
        },
        {
          props: { color: 'info' as const },
          style: {
            backgroundColor: palette.info.main,
            color: onAccent(palette.info.main),
          },
        },
        {
          props: { color: 'purple' as const },
          style: {
            backgroundColor: palette.purple.main,
            color: onAccent(palette.purple.main),
          },
        },
        {
          props: { color: 'cyan' as const },
          style: {
            backgroundColor: palette.cyan.main,
            color: onAccent(palette.cyan.main),
          },
        },
        {
          props: { color: 'teal' as const },
          style: {
            backgroundColor: palette.teal.main,
            color: onAccent(palette.teal.main),
          },
        },
      ],
    },
  }
}
